// Navigation structure and visibility metadata for the application shell.
import {
  LayoutDashboard, ShoppingCart, Package, Truck, FileText, Users, Building2, DollarSign,
  BarChart3, LogOut, Menu, X, User as UserIcon, ScanBarcode, UserCog, Landmark, Sparkles,
  BookOpen, Wallet, Settings as SettingsIcon, Inbox, MessageCircle, Receipt, TrendingDown,
  CreditCard, Users2, BookOpenCheck, Mail, ChevronDown, RotateCcw, Trophy, Banknote,
  ShieldAlert, Warehouse, ClipboardList, Repeat, Database, ScrollText, Activity, KeyRound,
  RefreshCw, Briefcase, Layers, ClipboardCheck, FileText as FileTextIcon, UserPlus, Clock,
  LifeBuoy, Gift, Utensils, ChefHat, QrCode, Stethoscope, CalendarCheck, HeartPulse, Ticket,
  Factory, ShieldCheck, Pill, Crown, ChevronRight, Share2, ArrowLeftRight, Send, FileSignature,
  FolderKanban,
} from "lucide-react";

// Feb 2026 — vertical groupings for menu visibility
// Office/service verticals with NO physical stock / POS
export const OFFICE_VERTICALS = ["school","university","training_center","ngo","charity","law_firm","engineering_office","real_estate","media_agency","accounting_firm"];
// Service verticals: POS-style billing OK, but wholesale/inventory-heavy features hidden
export const SERVICE_VERTICALS = ["services","car_service","laundry","salon","gym","hotel","transport"];
export const NO_POS = [...OFFICE_VERTICALS, "clinic","factory","restaurant","bakery"];
export const NO_WHOLESALE = [...OFFICE_VERTICALS, "clinic","restaurant","salon","gym","hotel"];
export const NO_PURCHASES = [...OFFICE_VERTICALS, "clinic"];
export const NO_INVENTORY = [...OFFICE_VERTICALS, "clinic"];
export const NO_DELIVERIES = [...OFFICE_VERTICALS, "clinic","restaurant","salon","gym"];

export const NAV_GROUPS = [
  { title: "الرئيسية", items: [
    { to: "/", key: "dashboard", icon: LayoutDashboard, end: true },
    { to: "/my-dashboard", key: "my_dashboard", icon: Trophy, roles: ["sales_rep","cashier"] },
    { to: "/guide", key: "guide", icon: BookOpenCheck },
  ]},
  { title: "العمليات اليومية", items: [
    { to: "/pos", key: "retail_pos", icon: ShoppingCart, feature: "cashier_pos", roles: ["super_admin","company_manager","accountant","cashier","pharmacist","sales_rep"], hidden_for: NO_POS },
    { to: "/wholesale", key: "wholesale", icon: Briefcase, roles: ["super_admin","company_manager","accountant","sales_rep"], hidden_for: NO_WHOLESALE },
    { to: "/invoices", key: "invoices", icon: FileText },
    { to: "/purchases", key: "purchases", icon: Truck, roles: ["super_admin","company_manager","accountant","warehouse","purchase_officer"], hidden_for: NO_PURCHASES },
    { to: "/purchase-orders", key: "purchase_orders", icon: ClipboardList, roles: ["super_admin","company_manager","accountant","warehouse","purchase_officer"], hidden_for: NO_PURCHASES },
    { to: "/returns/sale", key: "sale_returns", icon: RotateCcw, roles: ["super_admin","company_manager","accountant","cashier","pharmacist","sales_rep"], hidden_for: NO_POS },
    { to: "/returns/purchase", key: "purchase_returns", icon: RotateCcw, roles: ["super_admin","company_manager","accountant","warehouse","purchase_officer"], hidden_for: NO_PURCHASES },
    { to: "/quotations", key: "quotations", icon: FileTextIcon, roles: ["super_admin","company_manager","cashier","sales_rep"], hidden_for: ["clinic"] },
    { to: "/campaigns", key: "campaigns", icon: Sparkles, roles: ["super_admin","company_manager","accountant"], hidden_for: ["clinic","accounting_firm"] },
    { to: "/deliveries", key: "deliveries", icon: Truck, roles: ["super_admin","company_manager","accountant","warehouse","cashier"], hidden_for: NO_DELIVERIES },
    { to: "/recurring-invoices", key: "recurring_invoices", icon: Repeat, roles: ["super_admin","company_manager","accountant"] },
    { to: "/cash-reconciliation", key: "cash_reconciliation", icon: Wallet, roles: ["super_admin","company_manager","accountant","cashier","pharmacist"] },
    { to: "/customers", key: "customers", icon: Users },
    { to: "/suppliers", key: "suppliers", icon: Landmark, hidden_for: ["clinic"] },
  ]},
  { title: "المخزون", verticals_hidden: NO_INVENTORY, items: [
    { to: "/products", key: "products", icon: Package },
    { to: "/categories", key: "categories", icon: Layers, roles: ["super_admin","company_manager","warehouse"] },
    { to: "/labels", key: "labels", icon: ScanBarcode, roles: ["super_admin","company_manager","warehouse"] },
    { to: "/batches", key: "batches", icon: Layers, roles: ["super_admin","company_manager","warehouse","accountant","pharmacist"] },
    { to: "/warehouses", key: "warehouses", icon: Warehouse, feature: "warehouses", roles: ["super_admin","company_manager","warehouse","accountant","pharmacist"] },
    { to: "/banks", key: "banks", icon: Landmark, roles: ["super_admin","company_manager","accountant"] },
    { to: "/stock-count", key: "stock_count", icon: ClipboardCheck, feature: "warehouses", roles: ["super_admin","company_manager","warehouse"] },
    { to: "/prescriptions", key: "prescriptions", icon: FileTextIcon, roles: ["super_admin","company_manager","accountant","cashier","pharmacist"] },
  ]},
  { title: "المحاسبة والمالية", items: [
    { to: "/vouchers", key: "vouchers", icon: Receipt, roles: ["super_admin","company_manager","accountant","cashier","pharmacist"] },
    { to: "/cheques", key: "cheques", icon: Landmark, roles: ["super_admin","company_manager","accountant","cashier","pharmacist"] },
    { to: "/cashboxes", key: "cash_boxes", icon: Wallet, roles: ["super_admin","company_manager","accountant","cashier","pharmacist"] },
    { to: "/expenses", key: "expenses", icon: TrendingDown, roles: ["super_admin","company_manager","accountant"] },
    { to: "/accounting", key: "journal", icon: BookOpen, roles: ["super_admin","company_manager","accountant"] },
    { to: "/statements", key: "statements", icon: FileTextIcon, roles: ["super_admin","company_manager","accountant","auditor"] },
    { to: "/fixed-assets", key: "fixed_assets", icon: Briefcase, roles: ["super_admin","company_manager","accountant","auditor"] },
    { to: "/bank-recon", key: "bank_recon", icon: Landmark, roles: ["super_admin","company_manager","accountant","auditor"] },
    { to: "/cashboxes/report", key: "cashboxes_report", icon: Wallet, roles: ["super_admin","company_manager","accountant","auditor"] },
    { to: "/period-close", key: "period_close", icon: ShieldCheck, roles: ["super_admin","company_manager","accountant"] },
    { to: "/currencies", key: "currencies", icon: DollarSign, roles: ["super_admin","company_manager","accountant"] },
    { to: "/finance-extras", key: "finance_extras", icon: BarChart3, roles: ["super_admin","company_manager","accountant","auditor"] },
  ]},
  { title: "التقارير والذكاء الاصطناعي", items: [
    { to: "/reports", key: "reports", icon: BarChart3, feature: "reports" },
    { to: "/reports/returns-analytics", key: "returns_analytics", icon: RotateCcw, feature: "reports", roles: ["super_admin","company_manager","accountant","auditor"] },
    { to: "/reports/sales-rep-returns", key: "sales_rep_returns", icon: BarChart3, feature: "reports", roles: ["super_admin","company_manager","accountant","auditor"] },
    { to: "/reports/commission-payout", key: "commission_payout", icon: Banknote, feature: "reports", roles: ["super_admin","company_manager","accountant"] },
    { to: "/reports/fraud-detection", key: "fraud_detection", icon: ShieldAlert, feature: "reports", roles: ["super_admin","company_manager","accountant","auditor"] },
    { to: "/ai", key: "ai_chat", icon: Sparkles, feature: "ai_chat", roles: ["super_admin","company_manager","accountant"] },
    { to: "/chat", key: "chat", icon: MessageCircle },
  ]},
  { title: "الإدارة والإعدادات", items: [
    { to: "/users", key: "users", icon: UserCog, roles: ["super_admin","company_manager"] },
    { to: "/branches", key: "branches", icon: Building2, roles: ["super_admin","company_manager"] },
    { to: "/companies", key: "companies", icon: Building2, roles: ["super_admin"] },
    { to: "/sold-log", key: "sold_log", icon: Crown, roles: ["super_admin"] },
    { to: "/admins", key: "admins", icon: UserCog, roles: ["super_admin"] },
    { to: "/plans", key: "company_feature_restrictions", icon: CreditCard, roles: ["super_admin"] },
    { to: "/subscriptions", key: "company_feature_assignments", icon: Users2, roles: ["super_admin"] },
    { to: "/payments", key: "payments", icon: Receipt, roles: ["super_admin","company_manager","accountant"] },
    { to: "/firm", key: "firm", icon: Briefcase, roles: ["super_admin"] },
    { to: "/reminder-template", key: "reminder_template", icon: Mail, roles: ["super_admin","company_manager","accountant"] },
    { to: "/payroll", key: "payroll", icon: Users2, roles: ["super_admin","company_manager","accountant"] },
    { to: "/hr", key: "hr", icon: Users2, roles: ["super_admin","company_manager","accountant"] },
    { to: "/support", key: "chat", icon: Inbox, roles: ["super_admin"] },
    { to: "/account-requests", key: "account_requests", icon: UserPlus, roles: ["super_admin"] },
    { to: "/system", key: "system_settings", icon: SettingsIcon, roles: ["super_admin"] },
    { to: "/system-health", key: "system_health", icon: Activity, roles: ["super_admin","company_manager"] },
    { to: "/integrations", key: "integrations", icon: Share2, roles: ["super_admin","company_manager"] },
    { to: "/online-store", key: "online_store", icon: Share2, roles: ["super_admin","company_manager","cashier","warehouse","sales_rep"] },
    { to: "/store-approvals", key: "store_approvals", icon: Share2, roles: ["super_admin"] },
    { to: "/backup", key: "backup", icon: Database, roles: ["super_admin"] },
    { to: "/my-backup", key: "my_backup", icon: Database, roles: ["company_manager"] },
    { to: "/audit-log", key: "audit_log", icon: ScrollText, roles: ["super_admin","company_manager","auditor"] },
    { to: "/custom-roles", key: "custom_roles", icon: ShieldCheck, roles: ["super_admin","company_manager"] },
    { to: "/2fa", key: "two_fa", icon: KeyRound },
    { to: "/pending-users", key: "pending_users", icon: Clock, roles: ["super_admin"] },
  ]},
  { title: "فريق الدعم والمحاسبين", items: [
    { to: "/support/staff", key: "support_staff", icon: Users2, roles: ["super_admin"] },
    { to: "/support/roles", key: "support_roles", icon: ShieldAlert, roles: ["super_admin"] },
    { to: "/support/access", key: "support_access", icon: UserPlus, roles: ["super_admin","company_manager","support"] },
    { to: "/support/activity", key: "support_activity", icon: BookOpenCheck, roles: ["super_admin","company_manager","support"] },
  ]},
  { title: "خدمة العملاء", items: [
    { to: "/tickets", key: "tickets", icon: LifeBuoy },
    { to: "/referrals", key: "referrals", icon: Gift },
    { to: "/loyalty-vouchers", key: "loyalty_vouchers", icon: Ticket, roles: ["super_admin","company_manager","accountant","cashier","pharmacist"] },
  ]},
  { title: "🍽️ المطعم", verticals: ["restaurant","bakery"], items: [
    { to: "/restaurant/pos", key: "rest_pos", icon: ShoppingCart, verticals: ["restaurant","bakery"], roles: ["super_admin","company_manager","accountant","cashier","sales_rep","waiter"] },
    { to: "/restaurant/tables", key: "rest_tables", icon: Utensils, verticals: ["restaurant","bakery"], roles: ["super_admin","company_manager","accountant","cashier","pharmacist","sales_rep","waiter"] },
    { to: "/restaurant/kds", key: "rest_kds", icon: ChefHat, verticals: ["restaurant","bakery"], roles: ["super_admin","company_manager","accountant","cashier","pharmacist","waiter","chef"] },
    { to: "/restaurant/stations", key: "rest_stations", icon: Layers, verticals: ["restaurant","bakery"], roles: ["super_admin","company_manager"] },
    { to: "/restaurant/qr", key: "rest_qr", icon: QrCode, verticals: ["restaurant","bakery","supermarket","grocery"], roles: ["super_admin","company_manager"] },
  ]},
  { title: "🏭 المصنع", verticals: ["factory","bakery"], items: [
    { to: "/production", key: "production_orders", icon: Factory, verticals: ["factory","bakery"], roles: ["super_admin","company_manager","accountant","warehouse"] },
  ]},
  { title: "💊 الصيدلية", verticals: ["pharmacy"], items: [
    { to: "/pharmacy/dispensing", key: "pharmacy_dispensing", icon: Pill, verticals: ["pharmacy"], roles: ["super_admin","company_manager","accountant","pharmacist","cashier"] },
  ]},
  { title: "🩺 العيادة", verticals: ["clinic"], items: [
    { to: "/clinic/appointments", key: "clinic_appts", icon: CalendarCheck, verticals: ["clinic"], roles: ["super_admin","company_manager","accountant","doctor","nurse","receptionist"] },
    { to: "/clinic/patients", key: "clinic_patients", icon: HeartPulse, verticals: ["clinic"], roles: ["super_admin","company_manager","accountant","doctor","nurse","receptionist"] },
  ]},
  { title: "🏗️ التعهدات والمقاولات", verticals: ["construction"], items: [
    { to: "/construction", key: "construction_dashboard", icon: FolderKanban, verticals: ["construction"], roles: ["super_admin","company_manager","accountant"] },
    { to: "/construction/contracts", key: "construction_contracts", icon: FileSignature, verticals: ["construction"], roles: ["super_admin","company_manager","accountant"] },
  ]},
  { title: "💱 الصرافة والحوالات", verticals: ["exchange"], items: [
    { to: "/exchange", key: "exchange", icon: ArrowLeftRight, verticals: ["exchange"], roles: ["super_admin","company_manager","accountant","cashier","exchange_teller"] },
    { to: "/remittances", key: "remittances", icon: Send, verticals: ["exchange"], roles: ["super_admin","company_manager","accountant","cashier","exchange_teller"] },
  ]},
];
// Flat list kept for feature-flag lookups and role filtering
export const NAV = NAV_GROUPS.flatMap(g => g.items);

// Professional per-group color + emblem for the home "choose a section" screen
export const GROUP_META = {
  "الرئيسية": { color: "#4f46e5", icon: LayoutDashboard },
  "العمليات اليومية": { color: "#059669", icon: ShoppingCart },
  "المخزون": { color: "#d97706", icon: Package },
  "المحاسبة والمالية": { color: "#2563eb", icon: Wallet },
  "التقارير والذكاء الاصطناعي": { color: "#7c3aed", icon: BarChart3 },
  "الإدارة والإعدادات": { color: "#475569", icon: SettingsIcon },
  "فريق الدعم والمحاسبين": { color: "#0891b2", icon: Users2 },
  "خدمة العملاء": { color: "#e11d48", icon: LifeBuoy },
  "🍽️ المطعم": { color: "#ea580c", icon: Utensils },
  "🏭 المصنع": { color: "#52525b", icon: Factory },
  "💊 الصيدلية": { color: "#0d9488", icon: Pill },
  "🩺 العيادة": { color: "#dc2626", icon: Stethoscope },
  "💱 الصرافة والحوالات": { color: "#0d9488", icon: ArrowLeftRight },
  "التواصل والروابط الخارجية": { color: "#0284c7", icon: Share2 },
};
export const DEFAULT_META = { color: "#0d9488", icon: LayoutDashboard };
export const cleanGroupTitle = (tt) => tt.replace(/[🍽️🏭💊🩺]/g, "").trim();
export const GROUP_TITLE_KEY = {
  "الرئيسية": "grp_home", "العمليات اليومية": "grp_daily", "المخزون": "grp_inventory",
  "المحاسبة والمالية": "grp_finance", "التقارير والذكاء الاصطناعي": "grp_reports_ai",
  "الإدارة والإعدادات": "grp_admin", "فريق الدعم والمحاسبين": "grp_support_team",
  "خدمة العملاء": "grp_cs", "المطعم": "grp_restaurant", "المصنع": "grp_factory",
  "الصيدلية": "grp_pharmacy", "العيادة": "grp_clinic", "التواصل والروابط الخارجية": "grp_comm",
};

export const ROLE_LABELS = {
  super_admin: "مدير عام",
  company_manager: "مدير شركة",
  office_manager: "مدير مكتب",
  hr_officer: "موظف موارد بشرية",
  accountant: "محاسب",
  cashier: "كاشير",
  warehouse: "أمين مستودع",
  sales_rep: "مندوب مبيعات",
  purchase_officer: "موظف مشتريات",
  auditor: "مدقق / مراجع",
  support: "موظف دعم",
  waiter: "نادل",
  chef: "طاهي / شيف",
  doctor: "طبيب",
  nurse: "ممرّض / ممرّضة",
  receptionist: "موظف استقبال",
  pharmacist: "صيدلي",
  // Education
  teacher: "معلم / أستاذ",
  principal: "مدير مدرسة",
  registrar: "مسجل طلاب",
  student_affairs: "شؤون الطلاب",
  // NGO
  program_manager: "مدير برنامج",
  volunteer: "متطوع",
  fundraiser: "مسؤول تبرعات",
  // Hotel
  hotel_receptionist: "موظف استقبال فندق",
  housekeeping: "تدبير منزلي",
  concierge: "كونسيرج",
  // Services
  trainer: "مدرب رياضي",
  stylist: "مصفف شعر",
  mechanic: "ميكانيكي",
  technician: "فني",
  driver: "سائق",
  // Professionals
  engineer: "مهندس",
  architect: "معماري",
  lawyer: "محامي",
  consultant: "مستشار",
  // Construction
  foreman: "مشرف موقع",
  site_supervisor: "مسؤول موقع",
  // Agriculture
  farmer: "مزارع",
  agronomist: "مهندس زراعي",
};
